// client/src/components/Insights/tabs/FireTab.jsx
// Financial independence from a typical year: real cash and investments, and the
// same growth assumption for the years-to-FIRE figure and the chart.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CardContent, Grid, LinearProgress, Typography } from '@mui/material';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';
import { Briefcase, Clock, Flame, PiggyBank, TrendingUp, Wallet } from 'lucide-react';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { GlassCard, LoadingGrid, SectionTitle, StatCard } from '../shared/SharedComponents';
import { GRADIENT, alpha, gradientTextSx } from '../shared/constants';
import { getFireNumber } from '../../../services/insightsAPI';

const FireTab = () => {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['fireNumber'],
    queryFn: () => getFireNumber({}),
    select: (d) => d?.data || d,
  });

  if (isLoading) return <LoadingGrid n={4} height={150} />;
  if (error || !data) {
    return (
      <Alert severity="warning" action={<Button onClick={refetch} size="small">{t('aiInsights.common.retry', 'Retry')}</Button>}>
        {t('aiInsights.fireV2.loadError', 'Could not load your FIRE numbers.')}
      </Alert>
    );
  }

  const years = (y) => (y == null ? t('aiInsights.fireV2.notReached', 'not at this rate') : t('aiInsights.fireV2.years', { count: y, defaultValue: '{{count}} years' }));
  const returnPct = Math.round((data.assumptions?.returnRate || 0.07) * 100);
  const withdrawPct = Math.round((data.assumptions?.withdrawalRate || 0.04) * 100);
  const variants = [
    { key: 'lean', color: '#f59e0b', label: t('aiInsights.fire.lean', 'Lean FIRE'), desc: t('aiInsights.fireV2.leanDesc', '25× a year of needs only'), v: data.lean },
    { key: 'regular', color: '#ef4444', label: t('aiInsights.fire.regular', 'Regular FIRE'), desc: t('aiInsights.fireV2.regularDesc', '25× a year of all your spending'), v: data.regular },
    { key: 'fat', color: '#8b5cf6', label: t('aiInsights.fire.fat', 'Fat FIRE'), desc: t('aiInsights.fireV2.fatDesc', '1.5× regular, for a more generous life'), v: data.fat },
  ];
  const chart = (data.projection || []).map((r) => ({ ...r, label: t('aiInsights.fireV2.yearShort', { count: r.year, defaultValue: 'Y{{count}}' }) }));

  return (
    <Box>
      <SectionTitle icon={Flame}>{t('aiInsights.fire.title', 'FIRE Calculator — Financial Independence')}</SectionTitle>

      {data.limitedData && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {t('aiInsights.fireV2.limited', { count: data.window?.months || 0, defaultValue: 'Based on only {{count}} months of history, so treat these numbers as a first estimate.' })}
        </Alert>
      )}

      <GlassCard sx={{ background: GRADIENT, color: '#fff', mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 3, fontWeight: 700 }}>
            {t('aiInsights.fire.yourNumber', 'YOUR FIRE NUMBER')}
          </Typography>
          <Typography variant="h2" fontWeight={900} sx={{ fontSize: { xs: 36, md: 56 } }}>{formatAmount(data.fireNumber)}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            {t('aiInsights.fireV2.heroLine', {
              count: data.window?.months || 0,
              expenses: formatAmount(data.monthlyExpenses),
              savings: formatAmount(data.monthlySavings),
              rate: data.savingsRate,
              defaultValue: 'Last {{count}} months: you spend {{expenses}} and save {{savings}} a month ({{rate}}% of income).',
            })}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
            {data.yearsToFire == null
              ? t('aiInsights.fireV2.noPath', 'At the current savings rate you will not get there. Saving more is what moves this.')
              : t('aiInsights.fireV2.eta', { years: data.yearsToFire, defaultValue: 'About {{years}} years to financial independence' })}
          </Typography>
        </CardContent>
      </GlassCard>

      <Grid container spacing={2} mb={3}>
        {variants.map((f) => (
          <Grid item xs={12} md={4} key={f.key}>
            <GlassCard sx={{ borderTop: `3px solid ${f.color}` }}>
              <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                <Typography fontWeight={800}>{f.label}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>{f.desc}</Typography>
                <Typography variant="h5" fontWeight={900} sx={{ color: f.color }}>{formatAmount(f.v?.target || 0)}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={f.v?.progress || 0}
                  sx={{ height: 8, borderRadius: 4, my: 1.25, bgcolor: alpha(f.color, 0.12), '& .MuiLinearProgress-bar': { bgcolor: f.color } }}
                />
                <Typography variant="caption" fontWeight={700} color={f.color} component="div">
                  {t('aiInsights.fireV2.progress', { pct: f.v?.progress || 0, defaultValue: '{{pct}}% there' })} · {years(f.v?.years)}
                </Typography>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard label={t('aiInsights.fireV2.have', 'You have now')} value={formatAmount(data.currentSavings)} sub={t('aiInsights.fireV2.haveSub', { cash: formatAmount(data.cash), investments: formatAmount(data.investments), defaultValue: 'cash {{cash}} · investments {{investments}}' })} icon={Wallet} color="#10b981" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={t('aiInsights.fire.monthlySavings', 'Monthly Savings')} value={formatAmount(data.monthlySavings)} icon={PiggyBank} color="#3b82f6" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={t('aiInsights.fire.savingsRate', 'Savings Rate')} value={`${data.savingsRate}%`} icon={TrendingUp} color="#6366f1" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={t('aiInsights.fire.yearsToFire', 'Years to FIRE')} value={data.yearsToFire == null ? '—' : String(data.yearsToFire)} icon={Clock} color="#f59e0b" />
        </Grid>
      </Grid>

      {chart.length > 1 && (
        <GlassCard sx={{ mb: 3 }}>
          <CardContent>
            <Typography fontWeight={800} mb={2} sx={gradientTextSx}>
              {t('aiInsights.fireV2.chartTitle', { pct: returnPct, defaultValue: 'Projection at {{pct}}% a year after inflation' })}
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatAmount(v)} width={90} />
                <RTooltip formatter={(v) => formatAmount(v)} />
                <Legend />
                <Area type="monotone" dataKey="portfolio" stroke="#8b5cf6" fill={alpha('#8b5cf6', 0.15)} strokeWidth={2} name={t('aiInsights.fireV2.withGrowth', 'With growth')} />
                <Area type="monotone" dataKey="contributions" stroke="#3b82f6" fill={alpha('#3b82f6', 0.06)} strokeWidth={2} strokeDasharray="4 4" name={t('aiInsights.fireV2.contributions', 'What you put in')} />
                <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="8 4" dot={false} name={t('aiInsights.fireV2.target', 'FIRE number')} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </GlassCard>
      )}

      <GlassCard sx={{ borderLeft: '3px solid #f59e0b' }}>
        <CardContent>
          <Typography fontWeight={700} mb={1}>
            <Briefcase size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {t('aiInsights.fire.explainerTitle', 'What is FIRE?')}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
            {t('aiInsights.fireV2.explainer', {
              annual: formatAmount(data.annualExpenses),
              number: formatAmount(data.fireNumber),
              withdraw: withdrawPct,
              growth: returnPct,
              defaultValue: 'Financial independence means your investments can pay for your life. Your FIRE number is 25 times a year of spending ({{annual}}): {{number}}, which you could draw down at about {{withdraw}}% a year. The projection assumes {{growth}}% growth a year after inflation and counts your cash, which also holds your emergency fund. Not financial advice.',
            })}
          </Typography>
        </CardContent>
      </GlassCard>
    </Box>
  );
};

export default FireTab;
